import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { AdminLayout } from "./Admin";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Loader2, Save, Eye, Send } from "lucide-react";
import { toast } from "sonner";

const TEMPLATES = [
  { name: "booking-confirmation", label: "Booking Confirmation" },
  { name: "booking-cancellation", label: "Booking Cancellation" },
  { name: "booking-invoice-link", label: "Booking Invoice Link" },
];

const MERGE_TAGS = [
  "{{customerName}}", "{{firstName}}", "{{studioName}}",
  "{{startAt}}", "{{endAt}}", "{{totalPaid}}", "{{bookingId}}",
];

type Override = {
  template_name: string;
  enabled: boolean;
  mode: "fields" | "html";
  subject_override: string | null;
  fields: { introText?: string; outroText?: string; ctaLabel?: string; ctaUrl?: string; footerNote?: string };
  custom_body: string | null;
};

const blank = (name: string): Override => ({
  template_name: name, enabled: true, mode: "fields",
  subject_override: "", fields: {}, custom_body: "",
});

const AdminEmailTemplates = () => {
  const [selected, setSelected] = useState(TEMPLATES[0].name);
  const [ov, setOv] = useState<Override>(blank(TEMPLATES[0].name));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [sendingTest, setSendingTest] = useState(false);

  const load = async (name: string) => {
    setLoading(true);
    setPreviewHtml(null);
    const { data } = await supabase.from("email_template_overrides" as any).select("*").eq("template_name", name).maybeSingle();
    const row = data as any;
    if (row) {
      setOv({
        template_name: name,
        enabled: row.enabled ?? true,
        mode: (row.mode as any) ?? "fields",
        subject_override: row.subject_override ?? "",
        fields: row.fields ?? {},
        custom_body: row.custom_body ?? "",
      });
    } else {
      setOv(blank(name));
    }
    setLoading(false);
  };

  useEffect(() => { load(selected); }, [selected]);

  const save = async () => {
    setSaving(true);
    const { error } = await supabase.from("email_template_overrides" as any).upsert({
      template_name: ov.template_name,
      enabled: ov.enabled,
      mode: ov.mode,
      subject_override: ov.subject_override || null,
      fields: ov.fields,
      custom_body: ov.custom_body || null,
    }, { onConflict: "template_name" });
    setSaving(false);
    if (error) toast.error(error.message);
    else toast.success("Template saved");
  };

  const preview = async () => {
    setPreviewing(true);
    // Save current draft first so the preview reflects what we'd actually send.
    await save();
    const { data, error } = await supabase.functions.invoke("preview-transactional-email", { body: {} });
    setPreviewing(false);
    if (error) { toast.error(error.message); return; }
    const list = (data as any)?.templates ?? [];
    const match = list.find((t: any) => t.name === selected);
    if (!match?.html) { toast.error("No preview returned for this template."); return; }
    setPreviewHtml(match.html);
  };

  const sendTest = async () => {
    if (!testEmail || !/^.+@.+\..+$/.test(testEmail)) {
      toast.error("Enter a valid recipient email first.");
      return;
    }
    setSendingTest(true);
    // Save first so the test reflects the current draft.
    await save();
    const { data, error } = await supabase.functions.invoke("send-test-template-email", {
      body: { template_name: selected, recipient_email: testEmail },
    });
    setSendingTest(false);
    if (error) { toast.error(error.message); return; }
    if ((data as any)?.error) { toast.error((data as any).error); return; }
    toast.success(`Test ${selected} email sent to ${testEmail}`);
  };

  const setField = (k: string, v: string) =>
    setOv((o) => ({ ...o, fields: { ...o.fields, [k]: v } }));

  return (
    <AdminLayout title="EMAIL TEMPLATES">
      <p className="text-xs text-muted-foreground font-mono uppercase tracking-widest mb-4">
        Customize the subject + body of transactional emails. Merge tags update with each recipient.
      </p>

      <div className="flex gap-2 mb-6 flex-wrap">
        {TEMPLATES.map((t) => (
          <button key={t.name} onClick={() => setSelected(t.name)}
            className={`px-3 py-2 text-xs font-mono uppercase tracking-widest border ${
              selected === t.name ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-primary/50"
            }`}>{t.label}</button>
        ))}
      </div>

      {loading ? <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div> : (
        <div className="grid lg:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div className="bg-panel border border-border p-4 flex items-center justify-between">
              <div>
                <Label className="text-xs font-mono uppercase tracking-widest">Overrides enabled</Label>
                <p className="text-[11px] text-muted-foreground mt-1">Disable to fall back to the built-in default content.</p>
              </div>
              <Switch checked={ov.enabled} onCheckedChange={(v) => setOv((o) => ({ ...o, enabled: v }))} />
            </div>

            <div>
              <Label className="text-xs font-mono uppercase tracking-widest">Subject (optional)</Label>
              <Input value={ov.subject_override ?? ""} onChange={(e) => setOv((o) => ({ ...o, subject_override: e.target.value }))}
                placeholder="Booking confirmed — {{studioName}}" />
            </div>

            <Tabs value={ov.mode} onValueChange={(v) => setOv((o) => ({ ...o, mode: v as any }))}>
              <TabsList>
                <TabsTrigger value="fields">Simple fields</TabsTrigger>
                <TabsTrigger value="html">Advanced body</TabsTrigger>
              </TabsList>
              <TabsContent value="fields" className="space-y-3 mt-3">
                <div>
                  <Label className="text-xs font-mono uppercase tracking-widest">Intro text</Label>
                  <Textarea rows={3} value={ov.fields.introText ?? ""} onChange={(e) => setField("introText", e.target.value)}
                    placeholder="Hi {{firstName}}, your booking is locked in." />
                </div>
                <div>
                  <Label className="text-xs font-mono uppercase tracking-widest">Outro text</Label>
                  <Textarea rows={3} value={ov.fields.outroText ?? ""} onChange={(e) => setField("outroText", e.target.value)}
                    placeholder="See you soon!" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs font-mono uppercase tracking-widest">Button label</Label>
                    <Input value={ov.fields.ctaLabel ?? ""} onChange={(e) => setField("ctaLabel", e.target.value)} placeholder="VIEW MY BOOKINGS" />
                  </div>
                  <div>
                    <Label className="text-xs font-mono uppercase tracking-widest">Button URL</Label>
                    <Input value={ov.fields.ctaUrl ?? ""} onChange={(e) => setField("ctaUrl", e.target.value)} placeholder="https://bookmystudiospace.com/account" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs font-mono uppercase tracking-widest">Footer note</Label>
                  <Textarea rows={2} value={ov.fields.footerNote ?? ""} onChange={(e) => setField("footerNote", e.target.value)}
                    placeholder="Studio policy reminder, parking note, etc." />
                </div>
              </TabsContent>
              <TabsContent value="html" className="mt-3">
                <Label className="text-xs font-mono uppercase tracking-widest">Custom body (plain text · merge tags supported · paragraphs separated by blank lines)</Label>
                <Textarea rows={12} value={ov.custom_body ?? ""} onChange={(e) => setOv((o) => ({ ...o, custom_body: e.target.value }))}
                  placeholder={"Hi {{firstName}},\n\nThanks for booking {{studioName}} on {{startAt}}.\n\nYou paid ${{totalPaid}}. See you then."} />
              </TabsContent>
            </Tabs>

            <div className="bg-panel border border-border p-3 text-[11px] font-mono">
              <div className="text-muted-foreground uppercase tracking-widest mb-1">Merge tags</div>
              <div className="flex flex-wrap gap-1">
                {MERGE_TAGS.map((t) => <span key={t} className="px-2 py-1 border border-border">{t}</span>)}
              </div>
            </div>

            <div className="flex gap-2">
              <Button onClick={save} disabled={saving} className="bg-primary text-primary-foreground">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}Save
              </Button>
              <Button variant="outline" onClick={preview} disabled={previewing}>
                {previewing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Eye className="w-4 h-4 mr-2" />}Save & Preview
              </Button>
            </div>

            <div className="bg-panel border border-border p-4 space-y-2">
              <Label className="text-xs font-mono uppercase tracking-widest">Send test email</Label>
              <p className="text-[11px] text-muted-foreground">
                Saves the current draft, then sends this template with sample merge data to the address below.
              </p>
              <div className="flex gap-2">
                <Input
                  type="email"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  placeholder="you@example.com"
                />
                <Button onClick={sendTest} disabled={sendingTest} variant="outline">
                  {sendingTest ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                  Send test
                </Button>
              </div>
            </div>
          </div>

          <div className="bg-panel border border-border min-h-[500px]">
            <div className="border-b border-border px-4 py-2 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
              Preview {previewHtml ? "" : "(click Save & Preview to render)"}
            </div>
            {previewHtml ? (
              <iframe title="email preview" srcDoc={previewHtml} className="w-full h-[600px] bg-white" />
            ) : (
              <div className="p-8 text-sm text-muted-foreground">Save and click "Save & Preview" to render the email with sample data.</div>
            )}
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminEmailTemplates;